import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useField } from 'react-final-form';

import { formatScaleRange } from '../../util/scaleRange';
import { borderLabels, bordersToOrdinals, ordinalsToBorders } from '../../util/mandatoRanges';
import { RangeSlider } from '../../components';

import css from './FieldScaleRange.module.css';

/**
 * FASE2-WIZARD-2b-bis — Rango sobre una escala ordinal, con barra de dos topes.
 *
 * Un único control para las TRES dimensiones del mandato que se expresan como
 * intervalo (antigüedad, facturación y empleados). Se parametriza con su escala
 * —ANTIGUEDAD_SCALE, FACTURACION_SCALE, EMPLEADOS_SCALE—: las escalas ya son la
 * fuente única de tramos y ordinales, así que el control solo tiene que leerlas.
 *
 * POR QUÉ UNA BARRA Y NO DOS DESPLEGABLES:
 * los desplegables obligaban a elegir "Sin mínimo" y "Sin máximo" a mano para
 * decir "cualquiera", y hasta entonces el botón de publicar salía deshabilitado
 * sin explicar por qué. La barra arranca con los dos topes en los extremos, que
 * YA es un valor válido y significa exactamente eso, así que no bloquea nada.
 *
 * FASE2-WIZARD-2b-ter — LOS TOPES SE POSAN SOBRE FRONTERAS, NO SOBRE TRAMOS.
 * Cada posición es un corte entre tramos: con N tramos hay N+1 cortes, y lo
 * elegido es lo que queda entre los dos. Así los topes no pueden coincidir
 * —la separación mínima es un tramo entero— y desaparece el punto único que
 * antes representaba un rango. La conversión a los ordinales que se guardan
 * vive en util/mandatoRanges.js; el dato persistido no cambia.
 *
 * QUÉ SE GUARDA — nunca vacío: el ordinal del primer y del último tramo
 * cubiertos. Con la barra en los extremos son el primero y el último de la
 * escala.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.group grupo de MANDATO_RANGE_GROUPS (escala, claves y formato)
 * @param {string} props.label etiqueta del grupo
 * @param {string} props.minAriaLabel etiqueta accesible del tope izquierdo
 * @param {string} props.maxAriaLabel etiqueta accesible del tope derecho
 * @param {Object} props.intl
 */

// Ancho que se reserva a cada etiqueta para decidir cuántas caben. Estimado a
// partir de la más larga en lugar de medido: medir cada una obligaría a
// pintarlas todas primero y se verían saltar. ~7px por carácter a 13px de
// tamaño, más la separación mínima que evita que se toquen.
const LABEL_CHAR_PX = 7;
const LABEL_GAP_PX = 12;

export const visibleLabelIndices = (labels, width, active = []) => {
  const last = labels.length - 1;
  if (!width) {
    // Antes de medir se pintan todas: en escritorio es lo normal y evita el
    // parpadeo de aparecer a medias.
    return labels.map((_, i) => i);
  }
  const widest = labels.reduce((m, l) => Math.max(m, l.length), 0);
  const caben = Math.max(2, Math.floor(width / (widest * LABEL_CHAR_PX + LABEL_GAP_PX)));
  if (caben >= labels.length) {
    return labels.map((_, i) => i);
  }
  // Reparto regular. Los extremos se conservan siempre: son los que dicen
  // dónde empieza y dónde acaba la escala.
  const step = Math.ceil(last / (caben - 1));
  const regulares = [];
  for (let i = 0; i < last; i += step) {
    regulares.push(i);
  }
  // El último intermedio puede caer a menos de un paso del extremo derecho, y
  // como el extremo no se quita nunca, quedarían pegados: a 239px de ancho eso
  // dejaba "101" y "101+" a 8px. Si no guarda la distancia del reparto, sobra.
  if (last - regulares[regulares.length - 1] < step) {
    regulares.pop();
  }
  regulares.push(last);

  // El valor de cada tope SIEMPRE se ve: es lo que hace legible que hay dos y
  // no uno. Si cae en una frontera que el reparto había descartado, se
  // reinserta y se poda la vecina que quede demasiado cerca. Prioridad:
  // extremos y topes por delante de las etiquetas de reparto.
  const irrenunciables = new Set([0, last, ...active.filter(i => i >= 0 && i <= last)]);
  const candidatos = [...new Set([...regulares, ...irrenunciables])].sort((a, b) => a - b);

  return candidatos.reduce((out, i) => {
    const anterior = out[out.length - 1];
    if (anterior == null || i - anterior >= step) {
      return [...out, i];
    }
    // Demasiado juntas. Si la nueva es irrenunciable y la anterior no, la
    // sustituye; si ambas lo son (un tope pegado a un extremo, o los dos
    // topes contiguos), se muestran las dos: esa cercanía es el dato.
    if (irrenunciables.has(i)) {
      return irrenunciables.has(anterior) ? [...out, i] : [...out.slice(0, -1), i];
    }
    return out;
  }, []);
};

const FieldScaleRange = props => {
  const { className, rootClassName, group, label, minAriaLabel, maxAriaLabel, intl } = props;

  const { scale = [], minName, maxName } = group || {};
  const first = scale[0]?.idx;
  const last = scale[scale.length - 1]?.idx;
  // Fronteras: una más que tramos. La 0 es el suelo de la escala y la última
  // su techo abierto.
  const borderCount = scale.length;

  const { input: minInput } = useField(minName, { subscription: { value: true } });
  const { input: maxInput } = useField(maxName, { subscription: { value: true } });

  const labelsRef = useRef(null);
  const [labelsWidth, setLabelsWidth] = useState(0);

  useLayoutEffect(() => {
    const node = labelsRef.current;
    if (!node) {
      return undefined;
    }
    setLabelsWidth(node.clientWidth);
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(entries => {
      setLabelsWidth(entries[0].contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Los valores iniciales los pone el panel (extremos de la escala). El fallback
  // cubre un borrador guardado antes de existir este control.
  const toNum = (v, fallback) => {
    const n = Number(v);
    return v === '' || v == null || Number.isNaN(n) ? fallback : n;
  };
  const idxMin = toNum(minInput.value, first);
  const idxMax = toNum(maxInput.value, last);
  const [borderMin, borderMax] = ordinalsToBorders(idxMin, idxMax);

  const handleChange = handles => {
    const [nextIdxMin, nextIdxMax] = bordersToOrdinals(handles[0], handles[1]);
    if (nextIdxMin !== idxMin) {
      minInput.onChange(nextIdxMin);
    }
    if (nextIdxMax !== idxMax) {
      maxInput.onChange(nextIdxMax);
    }
  };

  const rangeText = formatScaleRange(group, idxMin, idxMax, intl);
  const labels = borderLabels(group);
  const shown = visibleLabelIndices(labels, labelsWidth, [borderMin, borderMax]);

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <span className={css.groupLabel}>{label}</span>

      <div className={css.sliderWrapper}>
        <RangeSlider
          min={0}
          max={borderCount}
          step={1}
          // Un tramo completo de separación: los topes nunca coinciden.
          minSeparation={1}
          marks={labels.map((_, i) => i)}
          handles={[borderMin, borderMax]}
          ariaLabels={[`${label}: ${minAriaLabel}`, `${label}: ${maxAriaLabel}`]}
          onChange={handleChange}
        />
      </div>

      {/* Valor de cada frontera, para que se vea dónde puede pararse el tope.
          En pantallas estrechas se muestran solo las que caben; el texto del
          rango, justo debajo, sigue diciendo el valor exacto.

          Las dos fronteras donde están los topes van resaltadas: es lo que
          evita que el control se lea como si tuviera un solo valor. Se
          resalta la etiqueta que YA está impresa en lugar de añadir un globo
          sobre cada tope, porque en facturación los topes contiguos quedan a
          33px y dos globos no caben sin pisarse. */}
      <div className={css.markLabels} ref={labelsRef} aria-hidden="true">
        {shown.map(i => (
          <span
            key={labels[i]}
            className={classNames(css.markLabel, {
              [css.markLabelActive]: i === borderMin || i === borderMax,
              [css.markLabelFirst]: i === 0,
              [css.markLabelLast]: i === labels.length - 1,
            })}
            style={{ left: `${(i / (labels.length - 1)) * 100}%` }}
          >
            {labels[i]}
          </span>
        ))}
      </div>

      {/* Lectura del rango, viva mientras se arrastra. Usa el MISMO formateador
          que la ficha del anuncio, para que publicar y leer digan lo mismo. */}
      <p className={css.rangeText} aria-live="polite">
        {rangeText}
      </p>
    </div>
  );
};

export default FieldScaleRange;
